/**
 * Service layer — image uploads (Supabase Storage), Round 10.2 → updated.
 *
 * Images upload to the `presentation-images` Supabase bucket. The public URL
 * is stored in Airtable attachment fields as before; ImageFrame detects Supabase
 * URLs and applies server-side image transforms for fast page loads (Pro plan).
 * Existing Cloudinary URLs in Airtable continue to work unchanged.
 */

const MAX_BYTES = 10 * 1024 * 1024

async function getUploadUrl(filename, action = 'presentationImageUploadUrl') {
  // Clerk bearer only. Uploads are a team edit-mode action; on the /present
  // route PresentationView sets window.__clerkGetToken when team=true. (The
  // legacy x-aj-token was removed 2026-07 — it shipped hardcoded in the bundle.)
  const clerk = typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null) : null
  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(clerk ? { Authorization: 'Bearer ' + clerk } : {}),
    },
    body: JSON.stringify({ action, filename }),
  })
  if (!res.ok) throw new Error('Upload URL request failed: ' + res.status)
  return res.json()
}

/** Upload a File/Blob to Supabase Storage. Returns { url, storagePath }. */
export async function uploadImage(fileOrBlob, filename = 'image.png') {
  if (fileOrBlob.size && fileOrBlob.size > MAX_BYTES) throw new Error('File too large (max 10MB)')
  const { signedUrl, path, publicUrl } = await getUploadUrl(filename)
  const up = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': fileOrBlob.type || 'image/png' },
    body: fileOrBlob,
  })
  if (!up.ok) throw new Error('Image upload failed: ' + up.status)
  return { url: publicUrl, storagePath: path }
}

/**
 * Ingest an external image URL: the server fetches it and stores OUR OWN copy in the
 * presentation-images bucket, so a pasted / Pinterest URL becomes a durable self-hosted
 * asset instead of a hotlink that breaks if the source is deleted. Returns { url,
 * storagePath }. Throws on failure so callers can fall back to keeping the raw URL.
 */
export async function uploadImageFromUrl(sourceUrl) {
  const clerk = typeof window.__clerkGetToken === 'function'
    ? await window.__clerkGetToken().catch(() => null) : null
  const res = await fetch('/api/upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(clerk ? { Authorization: 'Bearer ' + clerk } : {}),
    },
    body: JSON.stringify({ action: 'ingestImageUrl', url: sourceUrl }),
  })
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || ('Ingest failed: ' + res.status)) }
  return res.json()   // { url, storagePath }
}

/** Upload a base64 payload (from the AI endpoints). Returns { url, storagePath }. */
export async function uploadBase64(base64, mimeType = 'image/png', filename = 'ai-image.png') {
  const byteStr = atob(base64)
  const arr = new Uint8Array(byteStr.length)
  for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i)
  return uploadImage(new Blob([arr], { type: mimeType }), filename)
}

/**
 * GRAPHIC FIDELITY Round A (docs/bim-plan/18): upload a Modeler material texture
 * image into the `material-textures` bucket (its own bucket, not presentation-images
 * — firm-standard material assets, database/182). Returns { url, storagePath }.
 */
export async function uploadMaterialTexture(fileOrBlob, filename = 'texture.png') {
  if (fileOrBlob.size && fileOrBlob.size > MAX_BYTES) throw new Error('File too large (max 10MB)')
  const { signedUrl, path, publicUrl } = await getUploadUrl(filename, 'materialTextureUploadUrl')
  const up = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': fileOrBlob.type || 'image/png' },
    body: fileOrBlob,
  })
  if (!up.ok) throw new Error('Texture upload failed: ' + up.status)
  return { url: publicUrl, storagePath: path }
}

/** Round A: base64 variant (the AI material-swatch endpoint) → material-textures bucket. */
export async function uploadMaterialTextureBase64(base64, mimeType = 'image/png', filename = 'ai-texture.png') {
  const byteStr = atob(base64)
  const arr = new Uint8Array(byteStr.length)
  for (let i = 0; i < byteStr.length; i++) arr[i] = byteStr.charCodeAt(i)
  return uploadMaterialTexture(new Blob([arr], { type: mimeType }), filename)
}
